import { useI18n } from '@/i18n'
import { formatNumber } from '@/lib/utils'
import { operatorGlyph, unitForValue } from '@/features/report/computedFormat'
import type { ComputedValues, SourceRef } from '@/api/types'

/**
 * The computed values, exactly as the engine reported them — parameter, input
 * value, operator, limit — typeset the way an architect writes them:
 *
 *     baumasse_hauptbau_m3   1'649.86 m³ ≤ 1'650 m³
 *
 * in IBM Plex Mono with tabular figures, so a column of numbers scans.
 *
 * Nothing here recomputes or re-reads a comparison: the API hands over what the
 * deterministic engine calculated, and this component only lays it out. The
 * operator glyph and the unit are typography (see `computedFormat.ts`); the raw
 * operator stays available in `data-operator` so a test asserts the real thing.
 */
export function Computed({ computed }: { computed: ComputedValues | null | undefined }) {
  const { t } = useI18n()
  const bool = { yes: t('common.yes'), no: t('common.no') }
  if (!computed) return null

  const hasComparison = computed.parameter !== null && computed.parameter !== undefined
  const missing = computed.missing ?? computed.artifacts ?? null
  const hasConsistency = (computed.form_field ?? computed.gis_layer ?? null) !== null

  if (!hasComparison && !missing && !hasConsistency) return null

  const valueUnit = unitForValue(computed.parameter, computed.value)
  const limitUnit = unitForValue(computed.parameter, computed.limit)

  return (
    <dl
      data-testid="computed"
      className="flex flex-col gap-1 rounded-xs bg-surface px-3 py-2 text-[13px] print:bg-white print:px-0"
    >
      {hasComparison ? (
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-[12px] text-muted">{t('report.computed')}</dt>
          <dd
            data-testid="computed-comparison"
            data-parameter={computed.parameter}
            data-operator={computed.operator ?? undefined}
            className="num text-ink"
          >
            {computed.parameter} = {formatNumber(computed.value, bool)}
            {valueUnit ? ` ${valueUnit}` : ''}
            {computed.operator ? (
              <>
                {' '}
                <span className="text-muted-2">{operatorGlyph(computed.operator)}</span>{' '}
                {formatNumber(computed.limit, bool)}
                {limitUnit ? ` ${limitUnit}` : ''}
              </>
            ) : null}
          </dd>
        </div>
      ) : null}

      {hasConsistency ? (
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-[12px] text-muted">{t('report.computed')}</dt>
          <dd className="num text-ink">
            {computed.form_field ? (
              <>
                {computed.form_field} = {formatNumber(computed.form_value, bool)}
              </>
            ) : null}
            {computed.form_field && computed.gis_layer ? (
              <span className="text-muted-2"> · </span>
            ) : null}
            {computed.gis_layer ? (
              <>
                {computed.gis_layer} = {formatNumber(computed.gis_value, bool)}
              </>
            ) : null}
          </dd>
        </div>
      ) : null}

      {missing && missing.length > 0 ? (
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-[12px] text-muted">{t('report.missing')}</dt>
          <dd className="num text-ink">{missing.join(', ')}</dd>
        </div>
      ) : null}
    </dl>
  )
}

/**
 * Rule ID + § source + document version — mono, always visible on a finding.
 *
 * The version is printed as "Stand …" when the rulebase records one on the rule
 * itself, and is silently absent otherwise. It is never guessed by matching a
 * finding's `doc` string against the pack's source registry — the registry is
 * shown whole, in its own block on the report ("Quellen und Stand").
 */
export function SourceLine({
  ruleId,
  source,
  pack,
}: {
  ruleId?: string | null
  source?: SourceRef | null
  pack?: string | null
}) {
  const { t } = useI18n()
  const citation = source ? [source.doc, source.article].filter(Boolean).join(' · ') : ''

  return (
    <ul className="flex flex-col gap-0.5 text-[12px] text-muted">
      {ruleId ? (
        <li>
          <span className="text-muted-2">{t('report.rule')}</span>{' '}
          <code className="num text-ink-2">{ruleId}</code>
        </li>
      ) : null}
      {source && (citation || source.version) ? (
        <li>
          <span className="text-muted-2">{t('report.source')}</span>{' '}
          <span className="num text-ink-2">
            {citation}
            {source.version ? (
              <span data-testid="source-version" className="text-muted">
                {citation ? ', ' : ''}
                {t('report.asOf')} {source.version}
              </span>
            ) : null}
          </span>
        </li>
      ) : null}
      {pack ? (
        <li>
          <span className="text-muted-2">{t('report.pack')}</span>{' '}
          <code className="num text-ink-2">{pack}</code>
        </li>
      ) : null}
    </ul>
  )
}
